"use client";

import { type ReactNode, useRef } from "react";
import posthog from "posthog-js";
import { about } from "@/resources";

const CAL_NAMESPACE = "book-a-call";
const CAL_LINK = about.calendar.link.replace("https://cal.com/", "");

type CalApi = (action: string, options?: Record<string, unknown>) => void;

export function BookCall({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  // cal.com's embed.js sets a third-party cookie and ships real weight, so
  // it must not load with the page: it is fetched on hover/focus at the
  // earliest and the overlay opens programmatically on click.
  const calPromise = useRef<Promise<CalApi> | null>(null);

  const loadCal = () => {
    if (!calPromise.current) {
      calPromise.current = import("@calcom/embed-react")
        .then(({ getCalApi }) => getCalApi({ namespace: CAL_NAMESPACE }))
        .then((cal) => {
          cal("ui", {
            hideEventTypeDetails: false,
            cssVarsPerTheme: {
              dark: { "cal-brand": "#ffffff" },
              light: { "cal-brand": "#000000" },
            },
          });
          return cal as CalApi;
        });
    }
    return calPromise.current;
  };

  const openModal = async () => {
    posthog.capture("book_call_clicked");
    const cal = await loadCal();
    // the embed reads the config at open time, so the overlay follows the
    // site theme toggle without any observer bookkeeping
    const theme =
      document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
    cal("modal", {
      calLink: CAL_LINK,
      config: { layout: "month_view", theme },
    });
  };

  return (
    <button
      type="button"
      className={className}
      onPointerEnter={loadCal}
      onFocus={loadCal}
      onClick={openModal}
    >
      {children}
    </button>
  );
}
